import { Box, Button, styled, Typography } from "@material-ui/core";
import { encounterDisplayLabel } from "../../_lib/encounter-display-label";
import { UltrasoundScanner } from "../../_lib/epocus-icons";
import {
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_PRIMARY_DARK,
  EPOCUS_STATUS_IMPORTED_BG,
  EPOCUS_STATUS_IMPORTED_TEXT,
  EPOCUS_STATUS_ORDERED_BG,
  EPOCUS_STATUS_ORDERED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_TO_PERFORM_BG,
  EPOCUS_TO_PERFORM_BORDER,
  EPOCUS_URGENT_BG,
  EPOCUS_URGENT_BORDER,
  EPOCUS_URGENT_TEXT,
  EPOCUS_WHITE,
  rem,
} from "../../_lib/epocus-palette";
import { epocusTheme } from "../../_lib/epocus-theme-provider";
import { usePerformerName } from "../../_lib/hooks/use-performer-name";
import {
  relativeRequestAge,
  requestedExamTypeLabel,
} from "../../_lib/scan-request/scan-request-display";
import type { ToPerformEntry } from "./use-to-perform-queue";

const SectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 10,
});

const SectionTitle = styled(Typography)({
  fontSize: rem(16),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const CountBadge = styled(Box)({
  backgroundColor: EPOCUS_PRIMARY,
  color: EPOCUS_WHITE,
  borderRadius: 10,
  fontSize: rem(12),
  fontWeight: 700,
  minWidth: 20,
  height: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0 6px",
});

const RequestCard = styled(Box)({
  backgroundColor: EPOCUS_TO_PERFORM_BG,
  border: `1px solid ${EPOCUS_TO_PERFORM_BORDER}`,
  borderRadius: 12,
  padding: "12px 14px",
  display: "flex",
  flexDirection: "column",
  gap: 10,
  [epocusTheme.breakpoints.up("sm")]: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
});

const UrgentCard = styled(RequestCard)({
  borderColor: EPOCUS_URGENT_BORDER,
  borderLeft: `4px solid ${EPOCUS_URGENT_TEXT}`,
});

const CardMain = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 12,
  flex: 1,
  minWidth: 0,
});

const CardIcon = styled(Box)({
  width: 44,
  height: 44,
  borderRadius: 10,
  backgroundColor: EPOCUS_WHITE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  border: `1px solid ${EPOCUS_BORDER}`,
});

const CardInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 3,
});

const TitleRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 6,
  minWidth: 0,
});

const PatientName = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const DetailText = styled(Typography)({
  fontSize: rem(12),
  color: EPOCUS_TEXT_SECONDARY,
});

const IndicationText = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_PRIMARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const Tag = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  fontSize: rem(10),
  fontWeight: 700,
  letterSpacing: "0.06em",
  padding: "1px 6px",
  borderRadius: 4,
  flexShrink: 0,
});

const OrderedTag = styled(Tag)({
  backgroundColor: EPOCUS_STATUS_ORDERED_BG,
  color: EPOCUS_STATUS_ORDERED_TEXT,
});

const UrgentTag = styled(Tag)({
  backgroundColor: EPOCUS_URGENT_BG,
  color: EPOCUS_URGENT_TEXT,
});

const UnassignedTag = styled(Tag)({
  backgroundColor: EPOCUS_STATUS_IMPORTED_BG,
  color: EPOCUS_STATUS_IMPORTED_TEXT,
});

const StartScanButton = styled(Button)({
  backgroundColor: EPOCUS_PRIMARY,
  color: EPOCUS_WHITE,
  borderRadius: 8,
  fontSize: rem(12),
  fontWeight: 700,
  textTransform: "none",
  minHeight: 36,
  minWidth: 44,
  whiteSpace: "nowrap",
  flexShrink: 0,
  alignSelf: "flex-end",
  "&:hover": { backgroundColor: EPOCUS_PRIMARY_DARK },
  [epocusTheme.breakpoints.up("sm")]: { alignSelf: "center" },
});

const RequestedBy: FC<{ requestedById: string | null; age: string | null }> = ({
  requestedById,
  age,
}) => {
  const { displayName } = usePerformerName(requestedById);
  const requester = requestedById ? displayName : "a colleague";

  return (
    <DetailText>
      {age ? `Requested by ${requester} · ${age}` : `Requested by ${requester}`}
    </DetailText>
  );
};

const ToPerformRow: FC<{
  entry: ToPerformEntry;
  onStart: (encounterId: string) => void;
  starting: boolean;
}> = ({ entry, onStart, starting }) => {
  const { encounter, unassigned } = entry;
  const urgent = encounter.requestPriority === "urgent";
  const Card = urgent ? UrgentCard : RequestCard;
  const examLabel = requestedExamTypeLabel(encounter.requestedExamType);

  return (
    <Card>
      <CardMain>
        <CardIcon>
          <UltrasoundScanner
            width={24}
            height={24}
            fill={EPOCUS_ICON_ACTIVE}
            aria-hidden="true"
          />
        </CardIcon>
        <CardInfo>
          <TitleRow>
            <PatientName>{encounterDisplayLabel(encounter)}</PatientName>
            <OrderedTag>ORDERED</OrderedTag>
            {urgent && <UrgentTag>URGENT</UrgentTag>}
            {unassigned && <UnassignedTag>UNASSIGNED</UnassignedTag>}
          </TitleRow>
          {examLabel && <DetailText>{examLabel}</DetailText>}
          {encounter.indication && (
            <IndicationText>{encounter.indication}</IndicationText>
          )}
          <RequestedBy
            requestedById={encounter.requestedById}
            age={relativeRequestAge(encounter.requestedAt)}
          />
        </CardInfo>
      </CardMain>
      <StartScanButton
        variant="contained"
        disableElevation
        disabled={starting}
        onClick={() => onStart(encounter.id)}
      >
        Start scan
      </StartScanButton>
    </Card>
  );
};

export const ToPerformSection: FC<{
  entries: ReadonlyArray<ToPerformEntry>;
  onStart: (encounterId: string) => void;
  startingId: string | null;
}> = ({ entries, onStart, startingId }) => {
  if (entries.length === 0) return null;

  return (
    <Box>
      <SectionHeader>
        <SectionTitle>To Perform</SectionTitle>
        <CountBadge>{entries.length}</CountBadge>
      </SectionHeader>
      <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
        {entries.map((entry) => (
          <ToPerformRow
            key={entry.encounter.id}
            entry={entry}
            onStart={onStart}
            starting={startingId === entry.encounter.id}
          />
        ))}
      </Box>
    </Box>
  );
};
